import { useEffect, useRef, useState } from 'react'
import styles from './AcrossDisplays.module.css'

const DESCRIPTION =
  'Three-display diagram. A window moves from the current display to the left ' +
  'display for Shift plus four-finger swipe left, returns to center, then moves ' +
  'to the right display for Shift plus four-finger swipe right.'

/**
 * Lightweight explanatory animation for the two cross-display defaults.
 * It only runs while meaningfully visible; the mapping list below remains the
 * primary text alternative and reduced-motion users see a static center state.
 */
export function AcrossDisplays() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(false)

  useEffect(() => {
    const node = rootRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => setActive(entry.intersectionRatio >= 0.35),
      { threshold: [0, 0.35], rootMargin: '-8% 0px -8% 0px' }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={rootRef}
      className={styles.visual}
      data-active={active ? 'true' : 'false'}
      role="img"
      aria-label={DESCRIPTION}
    >
      <svg
        className={styles.diagram}
        viewBox="0 0 900 288"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="display-surface" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="rgba(255,255,255,0.035)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.008)" />
          </linearGradient>
          <linearGradient id="window-surface" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#222833" />
            <stop offset="1" stopColor="#141820" />
          </linearGradient>
        </defs>

        <g className={`${styles.display} ${styles.leftDisplay}`}>
          <rect x="24" y="42" width="252" height="164" rx="14" />
          <path d="M114 230h72M136 206v24M164 206v24" />
        </g>
        <g className={`${styles.display} ${styles.centerDisplay}`}>
          <rect x="324" y="30" width="252" height="176" rx="14" />
          <path d="M414 230h72M436 206v24M464 206v24" />
        </g>
        <g className={`${styles.display} ${styles.rightDisplay}`}>
          <rect x="624" y="42" width="252" height="164" rx="14" />
          <path d="M714 230h72M736 206v24M764 206v24" />
        </g>

        <rect className={`${styles.activeFrame} ${styles.leftActive}`} x="24" y="42" width="252" height="164" rx="14" />
        <rect className={`${styles.activeFrame} ${styles.rightActive}`} x="624" y="42" width="252" height="164" rx="14" />

        <g className={styles.window}>
          <rect className={styles.windowBody} x="368" y="68" width="164" height="106" rx="9" />
          <path className={styles.windowBar} d="M368 92h164" />
          <circle className={styles.windowDot} cx="383" cy="80" r="3" />
          <circle className={styles.windowDot} cx="394" cy="80" r="3" />
          <rect className={styles.windowLine} x="384" y="109" width="92" height="5" rx="2.5" />
          <rect className={styles.windowLine} x="384" y="124" width="128" height="5" rx="2.5" />
          <rect className={styles.windowLine} x="384" y="139" width="74" height="5" rx="2.5" />
        </g>
      </svg>

      <p className={styles.cue} aria-hidden="true">
        <span className={styles.leftCue}>Shift + four-finger swipe left</span>
        <span className={styles.centerCue}>Current display</span>
        <span className={styles.rightCue}>Shift + four-finger swipe right</span>
      </p>
    </div>
  )
}
